/* eslint-disable @typescript-eslint/no-unused-expressions */
import { ChangeEvent, ReactNode, useEffect } from 'react';
import { AppDispatch, RootState } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { IconButton, Stack, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TablePagination from '@mui/material/TablePagination';

import Colors from 'constants/colors';
import { tableOffSet } from 'appConfig';

import { resetTableState, updateTableState } from 'redux/slices/common/appSlice';
import { get } from 'utils/helpers';
import Button from 'components/Common/CustomButton';
import CustomPagination from 'components/Common/CustomPagination';
import NoResults from './NoResultsText';
import TableSkeleton from 'components/Skeleton/TableSkeleton';

export type Align = 'left' | 'right' | 'inherit' | 'justify' | 'center';
export interface Column {
    id: string;
    label?: string;
    minWidth?: number;
    align?: Align;
    format?: (value: number) => string;
    render?: ({ value, data }: any) => ReactNode;
    path?: string;
}

export interface Object {
    [key: string]: any;
}

interface StickyHeadTableProps {
    data: Object[];
    columns?: Column[];
    navigationHandler?: Function;
    successValue?: string;
    showDeleteCell?: boolean;
    onDeleteClick?: (value: any) => void;
    unStyled?: boolean;
    defaultPagination?: boolean;
    loadData?: any;
    onTemplatebuttonclick?: () => void;
    getStartedTemplate?: boolean;
    templateButtonText?: string;
    templateTitle?: string;
    parentOffset?: number;
    loading?: boolean;
    totalCount?: number;
    serverSidePagination?: boolean; // REMOVE THIS AND OPTIMIZE PAGINATION OF TABLE
    showArrowCell?: boolean;
    emptyRecordsMessage?: string;
}

export default function StickyHeadTable({
    data,
    columns,
    navigationHandler,
    successValue,
    showDeleteCell,
    onDeleteClick,
    unStyled,
    defaultPagination = true,
    loadData,
    onTemplatebuttonclick,
    getStartedTemplate,
    templateButtonText,
    templateTitle,
    parentOffset,
    totalCount,
    serverSidePagination,
    loading,
    showArrowCell = true,
    emptyRecordsMessage,
}: StickyHeadTableProps) {
    const dispatch = useDispatch<AppDispatch>();
    const { tableState } = useSelector((state: RootState) => ({ tableState: state.app.table }));
    const { pageSize, pageNumber, offset } = tableState || {};

    if (!serverSidePagination) {
        data = data?.slice(pageNumber * pageSize, pageNumber * pageSize + pageSize);
    }

    useEffect(() => {
        return () => {
            dispatch(resetTableState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (typeof parentOffset === 'number') {
            dispatch(updateTableState({ ...tableState, offset: parentOffset }));
        }
    }, [parentOffset, dispatch]); // eslint-disable-line

    const handleChangePage = (event: unknown, newPage: number) => {
        const _offset = pageNumber > newPage ? offset - tableOffSet : offset + tableOffSet;
        const updatedPagination = { pageSize, offset: _offset, pageNumber: newPage };
        loadData && loadData(updatedPagination);
        dispatch(updateTableState(updatedPagination));
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        const pageSize = Number(event.target.value);
        const updatedTableState = { ...tableState, pageSize, pageNumber: 0 };
        dispatch(updateTableState(updatedTableState));
        loadData && loadData(updatedTableState);
    };
    const getStyles = (rowValue: any) => {
        if (unStyled) {
            return {};
        }
        return { margin: '10px', backgroundColor: rowValue === 'Balance Withdraw' ? '#142456' : '#F2F2F6' };
    };
    if (!data?.length && getStartedTemplate) {
        return (
            <>
                <Stack width="100%" height="200px" justifyContent="center" alignItems="center">
                    <Typography sx={{ color: 'gray', fontSize: 35 }}>{templateTitle || "Let's get started!"}</Typography>
                    <Button
                        backgroundColor="none"
                        variant="outlined"
                        title={templateButtonText}
                        onClick={onTemplatebuttonclick}
                        sx={{ mt: 2, fontSize: 18 }}
                    />
                </Stack>
            </>
        );
    }
    if (!data?.length && !loading) {
        return (
            <>
                <NoResults message={emptyRecordsMessage} />
                {offset > 0 && (
                    <CustomPagination
                        offset={offset}
                        pageSize={pageSize}
                        total={totalCount || 0}
                        onPreviousPress={(e) => offset > 0 && handleChangePage(e, pageNumber - 1)}
                        onNextPress={(e) => handleChangePage(e, pageNumber + 1)}
                    />
                )}
            </>
        );
    }
    return (
        <>
            <TableContainer>
                <Table aria-label="sticky table" style={{ borderSpacing: '0 0.5rem', border: 0, borderCollapse: 'separate' }}>
                    <TableHead>
                        <TableRow
                            sx={{
                                '&.MuiTableRow-root': {
                                    'th:first-child': {
                                        borderRadius: '5px 0 0 5px',
                                    },
                                    'th:last-child': {
                                        borderRadius: '0 5px 5px 0',
                                    },
                                },
                            }}
                        >
                            {columns?.map((column) => (
                                <TableCell
                                    sx={{ borderCollapse: 'separate', borderSpacing: '10px' }}
                                    size="medium"
                                    key={column.id}
                                    align={column?.align || 'left'}
                                    style={{
                                        minWidth: column.minWidth,
                                        fontSize: 16,
                                        backgroundColor: Colors.tableRow,
                                        lineHeight: 1,
                                        color: '#6B7280',
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                            {showDeleteCell && <TableCell style={{ backgroundColor: '#F2F2F6' }}></TableCell>}{' '}
                            {/* This cell is for the delete icon */}
                            {showArrowCell && <TableCell style={{ backgroundColor: '#F2F2F6' }}></TableCell>}
                        </TableRow>
                    </TableHead>
                    {loading ? (
                        <div style={{ position: 'relative', height: '750px' }}>
                            <TableSkeleton
                                sx={{
                                    position: 'absolute',
                                }}
                            />
                        </div>
                    ) : (
                        <TableBody>
                            {data?.map((row, rowIndex) => {
                                const rowValue = row?.['from'];
                                return (
                                    <TableRow
                                        onClick={() => {
                                            navigationHandler && navigationHandler(row);
                                        }}
                                        sx={{
                                            marginBottom: '9px',
                                            '&.MuiTableRow-root': {
                                                '& td:first-child': {
                                                    borderTopLeftRadius: '5px',
                                                    borderBottomLeftRadius: '5px',
                                                },
                                                '& td:last-child': {
                                                    borderTopRightRadius: '5px',
                                                    borderBottomRightRadius: '5px',
                                                },
                                                '&:hover': {
                                                    backgroundColor: rowValue === 'Balance Withdraw' ? '#0A1640' : '#E5E7EB',
                                                    cursor: 'pointer',
                                                },
                                            },
                                            ...getStyles(rowValue),
                                        }}
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={rowIndex}
                                    >
                                        {columns?.map((column) => {
                                            const value = column.render
                                                ? column.render({ value: get(row, column.id, ''), data: row })
                                                : get(row, column?.path || column.id, '');
                                            const transactionStatus = row?.transactionStatus;
                                            return (
                                                <TableCell
                                                    style={{
                                                        margin: '5px 0px ',
                                                        fontSize: 16,
                                                        fontWeight: 400,
                                                        color:
                                                            transactionStatus === 'ERROR' || transactionStatus === 'FAILED'
                                                                ? 'red'
                                                                : value === 'Pending'
                                                                  ? '#DC883C'
                                                                  : value === successValue
                                                                    ? '#00A76A'
                                                                    : rowValue === 'Balance Withdraw'
                                                                      ? 'white'
                                                                      : '#1F2937',
                                                    }}
                                                    key={column.id}
                                                    align={column.align || 'left'}
                                                >
                                                    {column.format && typeof value === 'number' ? column.format(value) : value}
                                                </TableCell>
                                            );
                                        })}
                                        {showDeleteCell && (
                                            <TableCell
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteClick && onDeleteClick(row);
                                                }}
                                                align="right"
                                            >
                                                <IconButton sx={{ width: 24, height: 24 }}>
                                                    <DeleteForeverIcon fontSize="small" htmlColor="#D7172F" style={{ cursor: 'pointer' }} />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        {showArrowCell && (
                                            <TableCell align="right">
                                                <IconButton sx={{ height: 30, width: 30 }}>
                                                    <ArrowForwardIosIcon
                                                        htmlColor={rowValue === 'Balance Withdraw' ? 'white' : 'black'}
                                                        fontSize="small"
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
            {!defaultPagination ? (
                <CustomPagination
                    offset={offset}
                    pageSize={pageSize}
                    total={data.length || 0}
                    onPreviousPress={(e) => handleChangePage(e, pageNumber - 1)}
                    onNextPress={(e) => handleChangePage(e, pageNumber + 1)}
                />
            ) : (
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount || data?.length}
                    rowsPerPage={pageSize}
                    page={pageNumber}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}
        </>
    );
}
